import { useDroppable } from '@dnd-kit/core';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@uturn/api';
import { getFetchAllGroupQueryKey, useDeleteGroup } from '@uturn/api/v2';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	cn,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Icon,
	Separator,
	sonner,
} from '@uturn/ui-kit';
import { t } from 'i18next';
import React, { type PropsWithChildren, useState, useTransition } from 'react';
import { useGroupContext } from '../../context/group-context';
import type { TGroup } from '../../pages/overview';
import UpdateGroup from './update-group';

type Props = PropsWithChildren & {
	group: TGroup;
};

const Group: React.FC<Props> = ({ group, ...props }) => {
	const [openDeleteGroup, setOpenDeleteGroup] = useState(false);
	const queryClient = useQueryClient();
	const { isOver, setNodeRef } = useDroppable({
		id: group.groupId,
	});
	const [_, startTransition] = useTransition();
	const [open, setOpen] = useState(false);
	const { setGroups, setOptimisticGroups } = useGroupContext();
	const { mutateAsync: mutateDeleteGroup } = useDeleteGroup();

	const handleDeleteGroup = () => {
		setOptimisticGroups({
			type: 'REMOVE_GROUP',
			groupId: group.groupId,
		});

		startTransition(async () => {
			try {
				await mutateDeleteGroup(
					{
						id: group.groupId,
					},
					{
						onSuccess: async () => {
							sonner.success(
								t(
									'delete-group.toast.success-message',
									'Successfully deleted "{{name}}"',
									{
										name: group.groupName,
									},
								),
							);
							await queryClient.invalidateQueries({
								queryKey: getFetchAllGroupQueryKey(),
							});
						},
						onError: (error) => {
							if (isAxiosError(error)) {
								sonner.error(
									t(
										'delete-group.toast.error-message',
										'Failed to delete "{{name}}", please try again',
										{
											name: group.groupName,
										},
									),
								);
								return;
							}

							sonner.error(
								t(
									'toast.generic-error-message',
									'An unexpected error occurred. Please contact support',
								),
							);
						},
					},
				);

				setGroups((state) => {
					const result = [...state];

					return result.filter((groups) => groups.groupId !== group.groupId);
				});
			} catch (error) {
				console.error(error);
			}
		});
	};

	return (
		<>
			<div
				ref={setNodeRef}
				className={cn(
					'flex flex-col gap-2 px-2 py-4 min-h-[200px] bg-card shadow-sm border rounded-lg transition',
					isOver && 'scale-105',
					group.isPending && 'opacity-30',
				)}
			>
				<div className="flex items-center justify-between mx-2">
					<h3 className="text-muted-foreground">{group.groupName}</h3>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="xs">
								<Icon icon={faEllipsisVertical} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem onClick={() => setOpen(true)}>
								{t('group.context-menu.rename', 'Rename')}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setOpenDeleteGroup(true)}>
								{t('group.context-menu.delete', 'Delete')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<Separator />
				<div>{props.children}</div>
			</div>
			<UpdateGroup open={open} setOpen={setOpen} group={group} />
			<AlertDialog open={openDeleteGroup} onOpenChange={setOpenDeleteGroup}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('modal.alert.title', 'Are you sure?')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t(
								'modal.alert.delete-group.description',
								'This will delete group "{{ name }}", this action can not be undone.',
								{
									name: group.groupName,
								},
							)}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t('modal.alert.cancel', 'Cancel')}
						</AlertDialogCancel>
						<AlertDialogAction onClick={() => handleDeleteGroup()}>
							{t('modal.alert.confirm.delete', 'Yes, Delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};

export default Group;
